import { Aviso, Boton, Etiqueta, Modal, Tabla } from "../../components/ui/primitivos";
import { fetchPrecios, type PrecioListado } from "../../lib/consola-api";
import { useDatos } from "../../lib/useDatos";

/**
 * El historial de precios de UNA combinación tamaño × base.
 *
 * `imprenta` lo sirve por un `GET /precios/historial` aparte; acá sale del mismo
 * listado acotado con `tamanoId` y `baseId`, porque la vigencia temporal es el
 * modelo y no una vista (ADR 0012).
 *
 * Vive en `precios/` y no en `productos/` aunque las dos pantallas lo abran: es
 * una pregunta sobre la tarifa. La ficha de producto lo importa desde acá en vez
 * de tener su propia copia.
 */

export const TONO_VIGENCIA: Record<PrecioListado["estado"], "ok" | "aviso" | "neutral"> = {
  vigente: "ok",
  futuro: "aviso",
  expirado: "neutral",
};

export function HistorialPrecios({
  precio,
  onCerrar,
}: {
  precio: Pick<PrecioListado, "tamanoId" | "tamano" | "baseId" | "base">;
  onCerrar: () => void;
}) {
  const historial = useDatos(
    () => fetchPrecios("", "", { tamanoId: precio.tamanoId, baseId: precio.baseId }),
    `${precio.tamanoId}|${precio.baseId}`,
  );

  // Por tramo y después por fecha: así cada tramo se lee como una línea de
  // tiempo propia, que es la pregunta que trae a esta pantalla.
  const filas = [...(historial.datos?.precios ?? [])].sort((a, b) =>
    a.cantidadDesde !== b.cantidadDesde
      ? a.cantidadDesde - b.cantidadDesde
      : a.vigenteDesde.localeCompare(b.vigenteDesde),
  );

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={`${precio.tamano} · ${precio.base}`}
      descripcion="Todo lo que se cobró, se cobra y se va a cobrar por esta combinación"
    >
      <div className="contents">
        {historial.error ? <Aviso>{historial.error}</Aviso> : null}
        {historial.cargando ? (
          <p className="py-4 text-center text-neutral-500 text-sm">Cargando...</p>
        ) : filas.length === 0 ? (
          <p className="py-4 text-center text-neutral-500 text-sm">Sin precios cargados.</p>
        ) : (
          <Tabla cabeceras={["Tramo", "Precio", "VIP", "Desde", "Hasta", "Estado"]}>
            {filas.map((p) => (
              <tr key={p.id}>
                <td className="px-4 py-2 text-neutral-600 tabular-nums">
                  {p.cantidadDesde}–{p.cantidadHasta ?? "∞"}
                </td>
                <td className="px-4 py-2 tabular-nums">
                  {p.precio}
                  {p.esImporteFijo ? (
                    <span className="ml-1 text-neutral-500 text-xs">fijo</span>
                  ) : null}
                </td>
                <td className="px-4 py-2 text-neutral-600 tabular-nums">{p.precioVip ?? "—"}</td>
                <td className="px-4 py-2 text-neutral-600 text-xs">{p.vigenteDesde}</td>
                <td className="px-4 py-2 text-neutral-600 text-xs">
                  {p.vigenteHasta ?? "sin fin"}
                </td>
                <td className="px-4 py-2">
                  <Etiqueta tono={TONO_VIGENCIA[p.estado]}>{p.estado}</Etiqueta>
                </td>
              </tr>
            ))}
          </Tabla>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Boton variante="secundario" onClick={onCerrar}>
          Cerrar
        </Boton>
      </div>
    </Modal>
  );
}
